import { ChangeEvent, ReactElement, useContext, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Switch, Tab, Tabs,
  TextField,
} from "@mui/material";
import {
  IAcquisitionCell, IAcquisitionDataSchema,
} from "../types";
import { useSelector } from "react-redux";
import { ISelectedNode, IState, NullValue } from "../../../types";
import { SystemContext } from "../../../context/systemContext";
import TabPanel from "../../../components/tabPanel";
import VariablesTable from "./variablesTable";
import EventsTable from "./eventsTable";
import { getMachinesByNode } from "./utils";
import Select, { SelectChangeEvent } from "@mui/material/Select";

interface IProps {
  schema: IAcquisitionDataSchema;
  editableCell: IAcquisitionCell;
  setEditableCell: (cell: IAcquisitionCell) => void;
  disableEvents?: true;
}

const AcqConfigPanel = ({ editableCell, setEditableCell, schema, disableEvents }: IProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const systemContext = useContext(SystemContext);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [machines] = useState<ISelectedNode[]>(getMachinesByNode(selectedNode));
  const [selectedTab, setSelectedTab] = useState(0);


  const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setEditableCell({ ...editableCell, title: event.target.value });
  };

  const updateCharConfig = (field: string, value: any) => {
    setEditableCell({ ...editableCell, [field]: value === NullValue ? null : value });
  };

  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <div style={{ minHeight: 320, maxHeight: 640, width: 750 }}>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
        centered={true}
        style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
        <Tab label="General"/>
        <Tab label="Variables"/>
        {!disableEvents && !editableCell.indicatorMode && <Tab label="Events"/>}
      </Tabs>

      <TabPanel value={selectedTab} index={0}>
        <div className="display-flex-row mt-1">
          <TextField
            variant="standard"
            sx={{ margin: 1, minWidth: 284, width: 284 }}
            placeholder={"Title"}
            label={"Title"}
            value={editableCell.title}
            onChange={onchangeFilter}/>

          <FormControl sx={{ margin: 1, minWidth: 134, width: 134 }}>
            <InputLabel id="machine-label"
              sx={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>{systemContext.language.machine}</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={editableCell.machine ?? ""}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateCharConfig("machine", event.target.value)}>
              {machines.map((child) => {
                return <MenuItem key={`child-${child.nodeID}`} value={child.code}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>

          <div style={{
            display: "flex",
            flexDirection: "column",
            margin: 1,
            minWidth: 120,
            width: 120,
          }}>
            <InputLabel id="mode-label" sx={{ transform: "translate(0px, 6px) scale(0.75)" }}>Indicator
              Mode</InputLabel>
            <Switch checked={editableCell.indicatorMode ?? false}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCharConfig("indicatorMode", event.target.checked)}
              name="indicatorMode"/>
          </div>

          {editableCell.indicatorMode &&
            <div style={{
              display: "flex",
              flexDirection: "column",
              margin: 1,
              minWidth: 120,
              width: 120,
            }}>
              <InputLabel id="mode-label" sx={{ transform: "translate(0px, 6px) scale(0.75)" }}>
                Realtime
              </InputLabel>
              <Switch checked={editableCell.indicatorRealtime ?? false}
                onChange={(event: ChangeEvent<HTMLInputElement>) => updateCharConfig("indicatorRealtime", event.target.checked)}
                name="indicatorRealtime"/>
            </div>
          }

          <div style={{
            display: "flex",
            flexDirection: "column",
            margin: 1,
            minWidth: 120,
            width: 120,
          }}>
            <InputLabel id="axes-label" sx={{ transform: "translate(0px, 6px) scale(0.75)" }}>Stacked
              Axes</InputLabel>
            <Switch checked={editableCell.stackedAxes ?? false}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCharConfig("stackedAxes", event.target.checked)}
              name="Stacked Axes"/>
          </div>


        </div>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <VariablesTable variables={editableCell.variables}
          updateVariables={variables => updateCharConfig("variables", variables)}
          machine={editableCell.machine} indicatorMode={editableCell.indicatorMode === true}/>
      </TabPanel>

      {!editableCell.indicatorMode && <TabPanel value={selectedTab} index={2}>
        <EventsTable events={editableCell.events} updateEvents={events => updateCharConfig("events", events)}
          schema={schema}/>
      </TabPanel>}
    </div>
  );
};

export default AcqConfigPanel;
